/**
 * Hardcoded notices. Changing a noticeId re-shows that notice to every user.
 *
 * Interactive notices opt in with `interactable: true` and a `Content` component
 * in notices/. There is no interaction type enum — each notice owns its UI and
 * the shape of `data`. Completing an interactable notice means `data` is stored.
 *
 * Recurrence:
 *   Notices with `snoozeFor` are persistent until complete. There is no X,
 *   overlay dismiss, or "Not now". The snooze durations replace that leave
 *   path; Content (or the board footer if there is no Content) renders them.
 *   Incomplete `snoozeFor` notices use their own stacked overlay, not the
 *   standard board. X / paging on standard notices cannot close them.
 *   `remindAfterDays` is not used to decide when a snoozeFor notice appears.
 *   Those show on login unless `snoozedUntil` is still in the future, in which
 *   case they wait for the snooze timer. Legacy `viewedAt` without
 *   `snoozedUntil` is not a snooze. `remindAfterDays` remains the default
 *   snooze length only for notices without `snoozeFor`.
 *
 * Content props:
 *   notice     - this catalog entry
 *   savedData  - previously stored `data` object, or undefined
 *   name - first name for `{name}` in copy (and Content props)
 *   onSave(data)         - persist `data` and append `viewedAt` without closing
 *   onComplete(data?)    - persist `data` if given (ends recurrence), else default snooze, then close/advance
 *   onSnooze(ms)         - snooze for this duration, then close/advance
 *
 * Palette:
 *   Omit for the default green campaign chrome (Upload document primary).
 *   Use a preset name (`navy` | `green` | `purple` | `teal` | `blue`)
 *   or a partial object merged onto a preset:
 *     { preset: 'green', header: '#065f46' }
 *
 * Letter:
 *   Notices own their copy. Text bodies may use `{name}` anywhere.
 *
 * Audience:
 *   Omit `clinicId` to show the notice at every practice. Set it to a clinic
 *   id to show the notice only while that practice is selected.
 *
 * @typedef {Object} NoticePalette
 * @property {string} [preset]
 * @property {string} [header]
 * @property {string} [headerText]
 * @property {string} [wash]
 * @property {string} [accent]
 * @property {string} [accentSoft]
 *
 * @typedef {Object} Notice
 * @property {string} noticeId - Stable GUID; never change once shipped
 * @property {string} title
 * @property {string} [body]
 * @property {number} publishedAt - Epoch millis; newest first on the board
 * @property {boolean} visible - false retires the notice without wiping viewing history
 * @property {string} [clinicId] - Show only while this practice is selected
 * @property {boolean} [interactable] - true when Content can persist `data`
 * @property {number} [remindAfterDays] - default snooze after dismiss, in days
 * @property {NoticeSnoozeOption[]} [snoozeFor] - extra snooze durations the board presents
 * @property {string|NoticePalette} [palette]
 * @property {import('react').ComponentType<NoticeContentProps>} [Content]
 *
 * @typedef {Object} NoticeSnoozeOption
 * @property {string} [id]
 * @property {string} label
 * @property {number} ms
 *
 * @typedef {Object} NoticeContentProps
 * @property {Notice} notice
 * @property {object} [savedData]
 * @property {string} [name]
 * @property {(data: object) => void} onSave
 * @property {(data?: object) => void} onComplete
 * @property {(ms: number) => void} onSnooze
 */

import UserFeedbackSurveyContent from './notices/UserFeedbackSurvey.js';

export const NOTICES = [
  {
    noticeId: '4ae7eff0-75ac-45ea-ada8-e06792fac3de',
    title: 'An improved AI model for your pilot',
    body: 'Hi {name},\n\n'
      + 'Thank you for piloting our next-generation AI model. Following further evaluation, we have replaced it with an improved model, and The Heron Practice is the first practice anywhere to use it. It went live at midnight on Tuesday 3 November and applies to all documents processed since then. In our evaluations, it is more accurate at picking out key details such as NHS numbers and dates, while keeping the same summary style.\n\n'
      + 'There is nothing you need to do differently.\n\n'
      + 'Your team is helping to shape how MedPrompt works for every practice that follows, and we are genuinely grateful for that. Your feedback matters more than ever at this stage, so please do let us know if anything looks unexpected.\n\n'
      + 'Kind regards,\n\n'
      + 'Faraz Malik\nCTO\nMedGenix Tech',
    publishedAt: Date.UTC(2026, 10, 3),
    visible: true,
    clinicId: 'YjllYzZhNGQtYTIwYy00ODU0LWE2OGQtMTdiNjYyYmU3YjEz'
  },
  {
    noticeId: '03c2c008-9341-42af-81d2-ed6da579e052',
    title: 'Piloting our next-generation AI model',
    body: 'Hi {name},\n\n'
      + 'As part of our phased rollout, The Heron Practice is among the first to use our newest AI model for processing incoming documents. The new model went live at midnight on Monday 2 November and applies to all documents processed since then. In our evaluations, it delivers more complete and clinically precise summaries.\n\n'
      + 'There is nothing you need to do differently.\n\n'
      + 'Your feedback during this pilot will directly shape the wider rollout, so please do let us know if anything looks unexpected.\n\n'
      + 'Kind regards,\n\n'
      + 'Faraz Malik\nCTO\nMedGenix Tech',
    publishedAt: Date.UTC(2026, 10, 2),
    visible: true,
    clinicId: 'YjllYzZhNGQtYTIwYy00ODU0LWE2OGQtMTdiNjYyYmU3YjEz'
  },
  {
    noticeId: 'c3e8a91c-4f2d-4a7c-9e1b-6d5f8a0c2b47',
    title: 'MedPrompt User Feedback Survey',
    publishedAt: Date.UTC(2026, 7, 17),
    visible: false,
    interactable: true,
    remindAfterDays: 3,
    Content: UserFeedbackSurveyContent
  }
];
